import dayjs from './dayjs'
import sanitizeHtml from '../utils/sanitize'
import { BoardGroupFragment, BoardItemFragment } from '../generated/graphql'

// https://gist.github.com/gordonbrander/2230317#gistcomment-1618310
export function uniqueId(): string {
  function chr4() {
    return Math.random().toString(16).slice(-4)
  }
  return `${chr4() + chr4()}-${chr4()}-${chr4()}-${chr4()}-${chr4()}${chr4()}${chr4()}`
}

// Bound the add-item mutations: a request that never returns (e.g. a gateway timeout
// that hangs) would otherwise keep Apollo's `loading` true forever, wedging the
// in-flight guard and blocking the retry path (PLAN-1084).
//
// CAUTION: the abort only cancels the client request — the server may still commit
// the add after the abort, so a retry can insert a duplicate row. That gap closes
// with the clientRequestId idempotency key (PLAN-1084 WS3 follow-up); until then
// the timeout only trades a wedged UI for a small duplicate window on real timeouts.
const ADD_ITEM_TIMEOUT_MS = 30000

// AbortSignal.timeout is missing from this project's TS lib version; feature-detect it.
type AbortSignalWithTimeout = typeof AbortSignal & { timeout?: (ms: number) => AbortSignal }

// Built per call — an AbortSignal is single-use; a retry must get a fresh one.
export const addItemTimeoutContext = () => {
  const signalCtor =
    typeof AbortSignal !== 'undefined' ? (AbortSignal as AbortSignalWithTimeout) : undefined
  return typeof signalCtor?.timeout === 'function'
    ? { fetchOptions: { signal: signalCtor.timeout(ADD_ITEM_TIMEOUT_MS) } }
    : undefined
}

export const removeItemFromList = (
  itemFragment: BoardItemFragment,
  itemList: Array<BoardItemFragment>,
  previousPosition: number
): Array<BoardItemFragment> =>
  itemList
    .filter((item: BoardItemFragment) => item.id !== itemFragment?.id)
    .map((item: BoardItemFragment) => {
      const newItem = { ...item }
      if (item.displaySequence > previousPosition) {
        newItem.displaySequence -= 1
      }
      return newItem
    })

export const addItemToList = (
  itemFragment: BoardItemFragment,
  itemList: Array<BoardItemFragment>
): Array<BoardItemFragment> => [
  ...itemList
    .filter((item: BoardItemFragment) => item.id !== itemFragment?.id)
    .map((item: BoardItemFragment) => {
      const newItem = { ...item }
      if (newItem.displaySequence >= itemFragment.displaySequence) {
        newItem.displaySequence += 1
      }
      return newItem
    }),
  itemFragment
]

export function changeItemSequence<T extends BoardItemFragment | BoardGroupFragment>(
  fragment: T,
  itemList: Array<T>,
  previousPosition: number
): Array<T> {
  const newSequence = [
    ...itemList
      .filter((item: T) => item.id !== fragment?.id)
      .map((item: T) => {
        const newItem = { ...item }
        if (newItem.displaySequence > previousPosition) {
          newItem.displaySequence -= 1
        }
        if (newItem.displaySequence >= fragment.displaySequence) {
          newItem.displaySequence += 1
        }
        return newItem
      }),
    fragment
  ]
  const sorted = newSequence.sort(sortBy('displaySequence'))
  return sorted
}

/**
 * Used in conjunction with Array.sort to sort structured data.
 * https://github.com/you-dont-need/You-Dont-Need-Lodash-Underscore#_sortby-and-_orderby
 * @param key field to sort by.
 * @param isAsc sort ascending, otherwise descending.
 * @returns comparator result that determines the sort order
 */
export function sortBy(key: string, isAsc = true) {
  const greaterThanResult = isAsc ? 1 : -1
  const lessThanResult = isAsc ? -1 : 1
  return (a: any, b: any) =>
    // eslint-disable-next-line no-nested-ternary
    a[key] > b[key] ? greaterThanResult : b[key] > a[key] ? lessThanResult : 0
}

type Grouping<TItem> = {
  [key: string]: Array<TItem>
}
type GroupAccessor = {
  [key: string]: any
}

/**
 * Group a collection of objects by key
 * https://github.com/you-dont-need/You-Dont-Need-Lodash-Underscore#_groupby
 * https://gist.github.com/robmathers/1830ce09695f759bf2c4df15c29dd22d
 * @param data is an array of objects
 * @param key is the key (or property accessor) to group by
 */
export function groupBy<TItem>(data: TItem[], key: string): { [key: string]: TItem[] } {
  // reduce runs this anonymous function on each element of `data` (the `item` parameter,
  // returning the `storage` parameter at the end
  return data.reduce(
    (storage: Grouping<TItem>, item: GroupAccessor) => {
      // get the first instance of the key by which we're grouping
      const group = item[key]
      // set `storage` for this instance of group to the outer scope (if not empty) or initialize it
      // eslint-disable-next-line no-param-reassign
      storage[group] = storage[group] || []
      // add this item to its group within `storage`
      storage[group].push(item as TItem)
      // return the updated storage to the reduce function, which will then loop through the next
      return storage
    },
    // {} is the initial value of the storage
    {}
  )
}

// https://stackoverflow.com/a/32604073/1730140
export function camelCase(str: string) {
  // Lower cases the string
  return (
    str
      .toLowerCase()
      // Replaces any - or _ characters with a space
      .replace(/[-_]+/g, ' ')
      // Removes any non alphanumeric characters
      .replace(/[^\w\s]/g, '')
      // Uppercases the first character in each group immediately following a space
      // (delimited by spaces)
      .replace(/ (.)/g, ($1) => $1.toUpperCase())
      // Removes spaces
      .replace(/ /g, '')
  )
}

export enum DaysOfWeek {
  MONDAY = 'M',
  TUESDAY = 'Tu',
  WEDNESDAY = 'W',
  THURSDAY = 'Th',
  FRIDAY = 'F',
  SATURDAY = 'S',
  SUNDAY = 'Su'
}

export function daysOfWeekToAbbrevList(daysOfWeek: Array<DaysOfWeek>) {
  // This assures sorting regardless of provided list order or duplicates
  return Object.keys(DaysOfWeek).reduce((memo, key) => {
    let m = memo
    if (daysOfWeek.includes(key as DaysOfWeek)) {
      m += DaysOfWeek[key as keyof typeof DaysOfWeek]
    }
    return m
  }, '')
}

export function capitalizeFirstLetter(str: string) {
  if (!str) {
    return null
  }

  return str.charAt(0).toUpperCase() + str.slice(1)
}

export const validateEmail = (email: string) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)

/** Minimal shape sumItemCredits reads off a plan item. Structurally satisfied by BoardItemFragment. */
export type CreditSummable = Pick<
  BoardItemFragment,
  'subjectId' | 'creditsMin' | 'creditsMax' | 'nonCourseCreditsMin' | 'nonCourseCreditsMax'
>

/**
 * Sums a term's credits into a { min, max } range. Single source of truth for credit
 * totals — the interactive board (PlanGroupTitle) and the print view (PrintablePlan)
 * BOTH call this so the two surfaces can never disagree (CDE-1213).
 *
 * Non-course detection: an item is a "non-course" item iff `!item.subjectId`, in which
 * case its `nonCourseCredits*` are used; otherwise its `credits*` are used. Do NOT test
 * `nonCourseTitle` for this — a placeholder the advisor has filled with a real course
 * retains its `nonCourseTitle` but also acquires a `subjectId`, so testing `nonCourseTitle`
 * would wrongly keep the stale placeholder credits (the CDE-1213 divergence).
 *
 * Zero rule (PLAN-776): an item with min === 0 and max > 0 contributes its MAX to BOTH
 * the min-sum and the max-sum — it does NOT contribute 0 to the min. This is intentional;
 * removing it silently understates Term Total / Total Plan Credits. Example: an item of
 * { min: 0, max: 4 } contributes 4 to both totals.
 *
 * The rule is applied per item; callers just add the returned { min, max } together and
 * must not re-apply it at the aggregate level.
 */
export const sumItemCredits = (
  items: ReadonlyArray<CreditSummable>
): { min: number; max: number } => {
  const min = items.reduce((acc, item) => {
    if (!item?.subjectId && item.nonCourseCreditsMin && item.nonCourseCreditsMin > 0) {
      return acc + item.nonCourseCreditsMin
    }
    if (
      !item?.subjectId &&
      item.nonCourseCreditsMin === 0 &&
      item.nonCourseCreditsMax &&
      item.nonCourseCreditsMax > 0
    ) {
      return acc + item.nonCourseCreditsMax
    }
    if (item?.subjectId && item.creditsMin && item.creditsMin > 0) {
      return acc + item.creditsMin
    }
    if (item?.subjectId && item.creditsMin === 0 && item.creditsMax && item.creditsMax > 0) {
      return acc + item.creditsMax
    }
    return acc
  }, 0)

  const max = items.reduce((acc, item) => {
    if (!item?.subjectId && item.nonCourseCreditsMax && item.nonCourseCreditsMax > 0) {
      return acc + item.nonCourseCreditsMax
    }
    if (item?.subjectId && item.creditsMax && item.creditsMax > 0) {
      return acc + item.creditsMax
    }
    return acc
  }, 0)

  return { min, max }
}

export const formatCredits = (
  creditsMin?: number | null,
  creditsMax?: number | null
): number | string | null => {
  const min = creditsMin != null && creditsMin > 0 ? creditsMin : null
  const max = creditsMax != null && creditsMax > 0 ? creditsMax : null

  if (!min && !max) {
    return null
  }

  if (!min) {
    return max
  }

  if (!max) {
    return min
  }

  return min === max ? min : `${min} - ${max}`
}

/**
 * Custom sort functions to set null/undefined values last on asc order and first on desc
 * @param a first field to sort by.
 * @param b second field to sort by.
 * @param accessor table accessor to determine the actual string value for a and b.
 * @returns comparator result that determines the sort order
 */
export const sortTableStrings = (a: any, b: any, accessor: any) => {
  const A = typeof accessor === 'function' ? accessor(a.original, a.id) : a.original[accessor]

  const B = typeof accessor === 'function' ? accessor(b.original, b.id) : b.original[accessor]

  if (!A && B) {
    return 1
  }
  if (A && !B) {
    return -1
  }
  if ((A && A.toUpperCase()) < (B && B.toUpperCase())) {
    return -1
  }
  if ((A && A.toUpperCase()) > (B && B.toUpperCase())) {
    return 1
  }
  return 0
}

export const sortTableDates = (a: any, b: any, dateField: string) => {
  const {
    original: { [dateField]: dateA }
  } = a
  const {
    original: { [dateField]: dateB }
  } = b
  if (!dateA && dateB) {
    return -1
  }
  if (dateA && !dateB) {
    return 1
  }
  if (dayjs(dateA).isAfter(dayjs(dateB))) {
    return 1
  }
  if (dayjs(dateA).isBefore(dayjs(dateB))) {
    return -1
  }
  return 0
}

export enum Terms {
  FALL = 'FALL',
  AUTUMN = 'AUTUMN',
  QUARTER_ONE = 'QUARTER ONE',
  SEMESTER_ONE = 'SEMESTER ONE',
  TERM_ONE = 'TERM ONE',

  WINTER = 'WINTER',
  QUARTER_TWO = 'QUARTER TWO',
  SEMESTER_TWO = 'SEMESTER TWO',
  TERM_TWO = 'TERM TWO',

  SPRING = 'SPRING',
  QUARTER_THREE = 'QUARTER THREE',
  SEMESTER_THREE = 'SEMESTER THREE',
  TERM_THREE = 'TERM THREE',

  SUMMER = 'SUMMER',
  QUARTER_FOUR = 'QUARTER FOUR',
  SEMESTER_FOUR = 'SEMESTER FOUR',
  TERM_FOUR = 'TERM FOUR'
}

export const sortTermDates = (
  a: any,
  b: any,
  dateAccessor: string,
  yearAccessor: string,
  termAccessor: string
) => {
  const {
    original: { [yearAccessor]: yearA, [termAccessor]: termA, [dateAccessor]: dateA }
  } = a
  const {
    original: { [yearAccessor]: yearB, [termAccessor]: termB, [dateAccessor]: dateB }
  } = b

  // if a DATE is available compare dates
  if (dateA || dateB) {
    return sortTableDates(a, b, dateAccessor)
  }

  // Return 0 early when both rows are null
  if (!yearA && !termA && !yearB && !termB) {
    return 0
  }
  // Compare year value with null
  if (!yearA && yearB) {
    return -1
  }
  if (yearA && !yearB) {
    return 1
  }
  // Compare term value with null
  if (!yearA && !yearB) {
    if (!termA && termB) {
      return -1
    }
    if (termA && !termB) {
      return 1
    }
  }
  // Compare years
  if (yearA < yearB) {
    return -1
  }
  if (yearA > yearB) {
    return 1
  }
  // Compare terms within the same year based of the Terms Enum above
  if (yearA === yearB) {
    const termOrder = Object.values(Terms)
    if (termOrder.indexOf(termA.toUpperCase()) < termOrder.indexOf(termB.toUpperCase())) {
      return -1
    }
    if (termOrder.indexOf(termA.toUpperCase()) > termOrder.indexOf(termB.toUpperCase())) {
      return 1
    }
  }
  return 0
}

export const formatCodeDescriptionValue = (
  code: string,
  description: string | undefined | null
) => {
  if (!code && !description) {
    return null
  }
  if (code && !description) {
    return code
  }
  return `${code} - ${description}`
}

/**
 * Return the text from a HTML string
 *
 * @param {html} String The html string
 */
export function stripHtml(html: string | null | undefined) {
  if (!html) {
    return null
  }

  const tmpDiv = document.createElement('div')
  tmpDiv.innerHTML = html
  return tmpDiv.textContent || tmpDiv.innerText || ''
}

/**
 * Return true if a HTML is an empty text excluding blank lines or paragraphs
 *
 * @param {html} String The html string
 * @returns a boolean value that indicates if the input is an empty html or not
 */
export function isEmptyHtml(html: string | null | undefined): boolean {
  if (!html) {
    return true
  }
  return !stripHtml(html)?.length
}

/**
 * Find and replace hyperlink tags with escaped characters to a valid html code for rendering with dangerouslySetInnerHTML
 *
 * @param {html} String The html string
 * @returns the input html with unescaped code for <a href> tags
 */
export function findAndReplaceEscapedHyperlinks(html: string): string {
  const regexp = /(&lt;)\s*a(.*?)(&gt;)(.*?)(&lt;)\s*\/\s*a(&gt;)/g
  const found = html?.match(regexp)
  let replaced = html
  found?.forEach((str) => {
    replaced = replaced.replace(str, str.replaceAll('&lt;', '<').replaceAll('&gt;', '>'))
  })
  return replaced
}

/**
 * Return URL with https:// prepended
 * @param url
 * @returns URL prepended with https:// if it is not already.
 */
export function prependHttps(url: string | undefined) {
  if (!url) {
    return url
  }

  const trimmedUrl = url.trim()

  if (/^\.*\/|^(?!localhost)\w+?:/.test(url)) {
    return trimmedUrl
  }

  return trimmedUrl.replace(/^(?!(?:\w+?:)?\/\/)/, 'https://')
}

type ProfileName = {
  preferredName?: string | null
  firstName?: string | null
  lastName?: string | null
}

export function getDisplayName(profile: ProfileName | null | undefined) {
  if (!profile) {
    return ''
  }

  const preferredName =
    profile.preferredName && profile.firstName !== profile.preferredName
      ? `(${profile.preferredName}) `
      : ''
  const displayName = `${profile.firstName} ${preferredName}${profile.lastName}`
  return displayName
}

/**
 * Force a file download using a client-side constructed file Blob,
 * Based on:
 * https://github.com/mholt/PapaParse/issues/175#issuecomment-395978144
 * @param data string of data to download as a file
 * @param filename name of file
 * @param mimetype optional mimetype for download. Defaults to application/octet-stream
 */
export function openSaveFileDialog(data: string, filename: string, mimetype: string) {
  if (!data) {
    return
  }

  const blob =
    data.constructor !== Blob
      ? new Blob([data], { type: mimetype || 'application/octet-stream' })
      : data

  // msSaveBlob may exist on IE or Edge
  // @ts-ignore
  if (navigator.msSaveBlob) {
    // use file save API for IE
    // @ts-ignore
    navigator.msSaveBlob(blob, filename)
    return
  }

  // create a virtual anchor to make a click event
  // and force file save dialog
  const lnk = document.createElement('a')
  const url = window.URL

  if (mimetype) {
    lnk.type = mimetype
  }

  lnk.download = filename || 'untitled'
  const objectURL = url.createObjectURL(blob)
  lnk.href = objectURL
  lnk.dispatchEvent(new MouseEvent('click'))
  // release reference to the file
  setTimeout(url.revokeObjectURL.bind(url, objectURL))
}

// http://stackoverflow.com/a/19254521/1730140
export function stringFormat(str: string, scope: any, clean = true) {
  const reg = /{([^{}]+)}/g
  let match

  /* eslint-disable no-cond-assign, no-param-reassign */
  while ((match = reg.exec(str)) !== null) {
    const key = match[1].charAt(0).toLowerCase() + match[1].substring(1)

    str = str.replace(match[0], scope[key])
    reg.lastIndex = 0

    if (str.indexOf('undefined') !== -1 || str.indexOf('null') !== -1) {
      if (clean) {
        str = str.replace('undefined', '')
        str = str.replace('null', '')
      } else {
        // add temporary macro start/end symbols otherwise infinite loop happens
        str = str.replace('undefined', match[0].replace('{', '^|').replace('}', '*|'))
        str = str.replace('null', match[0].replace('{', '^|').replace('}', '*|'))
      }
    }
  }

  if (!clean) {
    str = str.replace('^|', '{').replace('*|', '}')
  }

  /* eslint-enable no-cond-assign, no-param-reassign */
  return str
}

// Standard date formatter for a date without a time, for consistency
export function formatDate(date: string | undefined) {
  if (!date) return '--'

  return dayjs(date).format('MMM DD, YYYY')
}

// Standard formatter for a date with a time
export function formatDateTime(dateTime: string | undefined) {
  if (!dateTime) return undefined

  return dayjs(dateTime).tz(dayjs.tz.guess()).format('MMM DD, YYYY h:mm A z')
}

export const formatTextWithHyperlink = (desc: string) => {
  if (typeof desc !== 'string') {
    return ''
  }

  return sanitizeHtml(desc, {
    allowedTags: ['a'],
    allowedAttributes: {
      a: ['href', 'target', 'rel']
    }
  })
}

export const formatNoHtml = (desc: string) =>
  // Do not allow html tags
  sanitizeHtml(desc)

/** Counts as they read in a sentence: "1 student", "3 students" */
export const pluralize = (count: number, singular: string, plural = `${singular}s`): string =>
  `${count} ${count === 1 ? singular : plural}`
